import React, { useState } from "react";
import ViewToggle from "./shared/ViewToggle";
import SearchBar from "./shared/SearchBar";
import BulkActionBar from "./shared/BulkActionBar";
import "./shared/shared.css";
import "./AppsPage.css";
import "./PromptLogsPage.css";

const PAGE_SIZE = 100;

export default function AppsPage({
  integrations,
  total,
  offset,
  onOffsetChange,
  loading,
  actionLoading,
  onEdit,
  onPublish,
  onApiKeys,
  onDoAction,
  onSelectApp,
  // Optional: pass an action gate from the parent. When `publishGate` is
  // `{ allowed: false, reason: "..." }` the Publish button is disabled
  // globally (e.g. while AI work is in progress on any app).
  publishGate,
  // Optional: handler that receives the source integration and opens a
  // modal asking for the new name. If omitted, the Duplicate button is
  // not rendered.
  onDuplicate,
  onShowDocs,
}) {
  const [view, setView] = useState(() => localStorage.getItem("forge-apps-view") || "card");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  // Search only filters within the currently-fetched page — the backend has
  // no search param, so cross-page search would require fetching everything.
  const filtered = integrations.filter((ig) => {
    if (search && !ig.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bulkAction = async (action, method) => {
    if (action === "remove" && !window.confirm(`Remove ${selected.size} apps? This cannot be undone.`)) return;
    for (const id of selected) {
      await onDoAction(id, action, method);
    }
    setSelected(new Set());
  };

  const handleGenerateDocs = (ig) => {
    if (onShowDocs) onShowDocs(ig.name);
  };

  const renderActions = (ig) => {
    return (
      <div className="td-actions">
        <button className="apps-btn apps-btn-edit" onClick={() => onEdit(ig)}>Edit</button>
        <button className="apps-btn apps-btn-preview" onClick={() => onSelectApp(ig)}>Preview</button>
        <button
          className="apps-btn apps-btn-docs"
          onClick={() => handleGenerateDocs(ig)}
        >
          Docs
        </button>
        <button className="apps-btn apps-btn-keys" onClick={() => onApiKeys(ig)}>API Keys</button>
        {onDuplicate && (
          <button
            className="apps-btn apps-btn-duplicate"
            onClick={() => onDuplicate(ig)}
            disabled={publishGate && !publishGate.allowed}
            title={publishGate && !publishGate.allowed ? publishGate.reason : "Create a new app starting from this one"}
          >
            Duplicate
          </button>
        )}
        <button
          className="apps-btn apps-btn-publish"
          onClick={() => onPublish(ig)}
          disabled={publishGate && !publishGate.allowed}
          title={publishGate && !publishGate.allowed ? publishGate.reason : undefined}
        >
          Publish
        </button>
        <button className="apps-btn apps-btn-danger" onClick={() => { if (window.confirm(`Remove "${ig.name}"?`)) onDoAction(ig.id || ig.name, "remove", "DELETE"); }}>Remove</button>
      </div>
    );
  };

  return (
    <div className="apps-page">
      <div className="page-header">
        <h2>My Apps</h2>
        <div className="page-header-right">
          <span className="apps-stat">{total} total</span>
        </div>
      </div>

      <div className="page-toolbar">
        <div className="page-toolbar-left" />
        <div className="page-toolbar-right">
          <SearchBar value={search} onChange={setSearch} placeholder="Search apps..." />
          <ViewToggle view={view} onChange={setView} storageKey="forge-apps-view" />
        </div>
      </div>

      <BulkActionBar
        count={selected.size}
        total={filtered.length}
        onSelectAll={() => setSelected(new Set(filtered.map((ig) => ig.id || ig.name)))}
        onDeselectAll={() => setSelected(new Set())}
      >
        <button className="bulk-btn-danger" onClick={() => bulkAction("remove", "DELETE")}>Remove</button>
      </BulkActionBar>

      <div className="page-body">
        {loading ? (
          <div className="page-empty">Loading apps...</div>
        ) : filtered.length === 0 ? (
          <div className="page-empty">
            {search ? `No apps matching "${search}"` : 'No apps yet. Click "+ New App" to create one.'}
          </div>
        ) : view === "table" ? (
          <table className="data-table">
            <thead>
              <tr>
                <th className="td-checkbox">
                  <input
                    type="checkbox"
                    checked={selected.size === filtered.length && filtered.length > 0}
                    onChange={(e) => setSelected(e.target.checked ? new Set(filtered.map((ig) => ig.id || ig.name)) : new Set())}
                  />
                </th>
                <th>Name</th>
                <th>Version</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ig) => (
                <tr key={ig.id || ig.name}>
                  <td className="td-checkbox">
                    <input type="checkbox" checked={selected.has(ig.id || ig.name)} onChange={() => toggleSelect(ig.id || ig.name)} />
                  </td>
                  <td>{ig.name}</td>
                  <td>{ig.version != null ? `v${ig.version}` : "—"}</td>
                  <td>{renderActions(ig)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="apps-grid">
            {filtered.map((ig) => (
              <div key={ig.id || ig.name} className="apps-card">
                <div className="apps-card-header">
                  <div className="apps-card-title">
                    <input type="checkbox" checked={selected.has(ig.id || ig.name)} onChange={() => toggleSelect(ig.id || ig.name)} />
                    <span className="apps-card-name">{ig.name}</span>
                  </div>
                  <div className="apps-card-badges">
                    {ig.version != null && <span className="apps-card-version">v{ig.version}</span>}
                  </div>
                </div>
                <div className="apps-card-actions">{renderActions(ig)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="prompt-logs-pagination">
          <button
            disabled={offset === 0}
            onClick={() => onOffsetChange(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </button>
          <span className="prompt-logs-page-info">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <button
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => onOffsetChange(offset + PAGE_SIZE)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
